import { useEffect, useRef, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { z } from 'zod';

import Input from 'components/Input/Input';
import Typography from 'components/Typography/Typography';
import { FormControl, FormField, FormItem } from 'components/ui/form';

import { templateStudioSchema } from '../validation-schema';

export const TitleInput: React.FC = () => {
  const { control, watch, setValue } =
    useFormContext<z.infer<typeof templateStudioSchema>>();

  const [isEditing, setIsEditing] = useState(false);
  const [originalValue, setOriginalValue] = useState<string>('');
  const [inputWidth, setInputWidth] = useState<number>(185);
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [isEditing]);

  const measureTextWidth = (text: string) => {
    if (measureRef.current) {
      measureRef.current.textContent = text || 'Template Name';
      const width = measureRef.current.offsetWidth;
      return Math.max(width + 20, 185); // Add padding and ensure minimum width
    }
    return 185;
  };

  const handleStartEditing = () => {
    const currentValue = watch('templateName') || '';
    setOriginalValue(currentValue);
    setInputWidth(measureTextWidth(currentValue));
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleCancel = () => {
    setValue('templateName', originalValue);
    setIsEditing(false);
  };

  const handleInputChange = (value: string) => {
    setInputWidth(measureTextWidth(value));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  return (
    <>
      {/* Hidden element for measuring text width */}
      <span
        ref={measureRef}
        className="absolute invisible whitespace-nowrap text-[14px] font-[inherit] select-text"
        aria-hidden="true"
      />
      {isEditing ? (
        <FormField
          control={control}
          name="templateName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  isRequired
                  placeholder="Template Name"
                  {...field}
                  ref={inputRef}
                  className="border-none"
                  style={{ width: `${inputWidth}px` }}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSave}
                  onChange={(e) => {
                    field.onChange(e);
                    handleInputChange(e.target.value);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      ) : (
        <>
          <div
            className="flex items-center justify-between gap-1 group hover:cursor-pointer border-l pl-4 border-base-border"
            onClick={handleStartEditing}
          >
            <Typography className="text-p-s font-semibold text-base-mutedForeground border border-transparent">
              {watch('templateName')}
            </Typography>
            <div className="cursor-pointer text-base-mutedForeground hidden group-hover:block ml-1">
              <FontAwesomeIcon icon="fa-solid fa-pen" />
            </div>
          </div>
        </>
      )}
    </>
  );
};
